import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Users, Link2, Copy, CheckCircle2, Eye, Clock, Printer, LayoutDashboard, MessageSquareHeart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OrgPanel } from '../components/OrgPanel';
import { TeamPlanPanel } from '../components/TeamPlanPanel';
import { api, API } from '../lib/api';
import { toast } from 'sonner';

const STATUS_LABEL = { not_started: 'Not started', researching: 'Researching', preparing_docs: 'Preparing documents', submitted: 'Submitted', under_review: 'Under review', approved: 'Approved', denied: 'Denied' };
const STATUS_COLOR = { submitted: 'bg-[#EDE9FE] text-[#7C3AED]', under_review: 'bg-[#DBEAFE] text-[#2563EB]', approved: 'bg-[#DCFCE7] text-[#16A34A]', denied: 'bg-[#FEE2E2] text-[#DC2626]', preparing_docs: 'bg-[#FEF3C7] text-[#D97706]', researching: 'bg-[#E0F2FE] text-[#0284C7]' };

const ShareProgress = ({ shareId }) => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/share/${shareId}/progress`).then(({ data }) => setP(data)).catch(() => {}); }, [shareId]);
  if (!p) return <p className="text-xs text-muted-foreground">Loading progress…</p>;
  const done = p.items.filter((i) => ['submitted', 'under_review', 'approved'].includes(i.status)).length;
  return (
    <div className="mt-2 rounded-lg bg-secondary p-3" data-testid={`share-progress-${shareId}`}>
      {!p.claimed ? <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Client hasn't opened and saved this checklist yet.</p> : p.sharing_paused ? <p className="text-xs text-[#663C00] flex items-center gap-1.5" data-testid={`share-paused-${shareId}`}><Clock className="w-3.5 h-3.5" /> Client has paused progress sharing. They claimed the checklist on {new Date(p.claimed_at).toLocaleDateString()}.</p> : (
        <>
          <div className="text-xs font-medium mb-2">{done} of {p.items.length} submitted or further · claimed {new Date(p.claimed_at).toLocaleDateString()}</div>
          <ul className="space-y-1.5">
            {p.items.map((i) => (
              <li key={i.program_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`share-progress-item-${i.program_id}`}>
                <span className="truncate">{i.name}</span>
                <Badge className={`border-0 shrink-0 ${STATUS_COLOR[i.status] || 'bg-[#F5F5F4] text-[#78716C]'}`}>{STATUS_LABEL[i.status] || 'Not started'}</Badge>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const NoteComposer = ({ shareId, claimed }) => {
  const [text, setText] = useState('');
  const [sent, setSent] = useState([]);
  useEffect(() => { api.get(`/share/${shareId}/messages`).then(({ data }) => setSent(data.messages)).catch(() => {}); }, [shareId]);
  const send = async () => {
    if (!text.trim()) return;
    try { const { data } = await api.post(`/share/${shareId}/message`, { text }); setSent((c) => [data, ...c]); setText(''); toast.success('Note sent — it will appear on your client\u2019s tracker'); }
    catch { toast.error('Could not send the note'); }
  };
  return (
    <div className="mt-2 rounded-lg border border-border p-3" data-testid={`note-composer-${shareId}`}>
      <div className="text-xs font-medium mb-1.5 flex items-center gap-1.5"><MessageSquareHeart className="w-3.5 h-3.5 text-[#1E5888]" /> Encouraging note for your client {!claimed && <span className="text-muted-foreground">(they\u2019ll see it once they claim the checklist)</span>}</div>
      <Textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 300))} rows={2} placeholder="e.g. Great progress on the DTC form — bring it Thursday and we\u2019ll finish Part B together." data-testid={`note-input-${shareId}`} />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-muted-foreground">{text.length}/300</span>
        <Button size="sm" onClick={send} disabled={!text.trim()} className="h-8 bg-[#1E5888] hover:bg-[#1E5888]/90 text-white" data-testid={`note-send-${shareId}`}>Send note</Button>
      </div>
      {sent.length > 0 && <ul className="mt-2 space-y-1">{sent.slice(0, 3).map((m) => <li key={m.message_id} className="text-xs text-muted-foreground" data-testid={`note-sent-${m.message_id}`}>“{m.text}” · {new Date(m.created_at).toLocaleDateString()}{m.read ? ' · read' : ''}</li>)}</ul>}
    </div>
  );
};

export default function PartnerMode() {
  const [programs, setPrograms] = useState([]);
  const [sel, setSel] = useState({});
  const [form, setForm] = useState({ client_name: '', worker_name: '', organization: '', notes: '' });
  const [created, setCreated] = useState(null);
  const [mine, setMine] = useState([]);
  const [openProgress, setOpenProgress] = useState(null);

  const loadMine = () => api.get('/share/mine').then(({ data }) => setMine(data.shares)).catch(() => {});
  useEffect(() => { api.get('/programs').then(({ data }) => setPrograms(data.programs)); loadMine(); }, []);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const linkFor = (id) => `${window.location.origin}/handoff/${id}`;

  const create = async () => {
    if (!form.client_name.trim()) return toast.error('Enter the client\u2019s first name or initials');
    if (!ids.length) return toast.error('Select at least one program');
    try {
      const { data } = await api.post('/share', { ...form, program_ids: ids });
      setCreated(data);
      setSel({}); setForm((f) => ({ ...f, client_name: '', notes: '' }));
      loadMine();
      toast.success('Shareable checklist created');
    } catch { toast.error('Could not create the link'); }
  };

  const copy = (id) => { navigator.clipboard?.writeText(linkFor(id)); toast.success('Link copied'); };

  const cats = [...new Set(programs.map((p) => p.category))];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E5888]/10 text-[#1E5888] text-xs font-medium mb-4"><Users className="w-3.5 h-3.5" /> Community Partner Mode</div>
      <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>Build a checklist for your client</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">Social workers and community agencies: pick the programs your client should pursue, add notes, and share a link. When your client opens it and signs in, everything lands in their own application tracker. Use first names or initials only — no SIN or health numbers.</p>
      <Button asChild variant="outline" className="mt-4" data-testid="partner-dashboard-link"><Link to="/partner/dashboard"><LayoutDashboard className="w-4 h-4 mr-2" /> Caseworker dashboard — all clients at a glance</Link></Button>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6 mt-8">
        <Card className="p-5 sm:p-6 bg-card">
          <h2 className="text-lg font-semibold mb-3" style={{fontFamily: 'Lexend'}}>1. Choose programs <Badge variant="outline" className="ml-2" data-testid="partner-selected-count">{ids.length} selected</Badge></h2>
          <div className="space-y-5">
            {cats.map((c) => (
              <div key={c}>
                <div className="text-sm font-medium text-muted-foreground mb-2">{c}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {programs.filter((p) => p.category === c).map((p) => (
                    <label key={p.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${sel[p.id] ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary'}`} data-testid={`partner-program-${p.id}`}>
                      <Checkbox checked={!!sel[p.id]} onCheckedChange={() => toggle(p.id)} className="mt-0.5" />
                      <div>
                        <div className="text-sm font-medium leading-snug">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.government} · {p.estimated_amount}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-5 bg-card">
            <h2 className="text-lg font-semibold mb-3" style={{fontFamily: 'Lexend'}}>2. Add details</h2>
            <div className="space-y-3">
              <div><label className="text-sm font-medium block mb-1">Client first name or initials *</label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="h-11" data-testid="partner-client-name" placeholder="e.g. Maria or M.K." /></div>
              <div><label className="text-sm font-medium block mb-1">Your name</label><Input value={form.worker_name} onChange={(e) => setForm({ ...form, worker_name: e.target.value })} className="h-11" data-testid="partner-worker-name" /></div>
              <div><label className="text-sm font-medium block mb-1">Organization</label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="h-11" data-testid="partner-org" placeholder="e.g. Fred Victor" /></div>
              <div><label className="text-sm font-medium block mb-1">Notes for your client</label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="partner-notes" placeholder="Bring your OHIP card and last Notice of Assessment to our next meeting." /></div>
              <Button onClick={create} className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="partner-create-link"><Link2 className="w-4 h-4 mr-2" /> Create shareable link</Button>
            </div>
          </Card>

          <OrgPanel />
          <TeamPlanPanel />

          {created && (
            <Card className="p-5 bg-[#E6F4EA] border-[#A3CFBB]" data-testid="partner-created">
              <div className="flex items-center gap-2 font-semibold text-[#0F5132]"><CheckCircle2 className="w-4 h-4" /> Link ready for {created.client_name}</div>
              <div className="mt-2 text-xs break-all bg-white/70 rounded p-2 border border-[#A3CFBB]" data-testid="partner-share-url">{linkFor(created.share_id)}</div>
              <Button size="sm" variant="outline" className="mt-2 bg-white" onClick={() => copy(created.share_id)} data-testid="partner-copy-link"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link</Button>
              <Button asChild size="sm" variant="outline" className="mt-2 ml-2 bg-white" data-testid="partner-created-pdf"><a href={`${API}/share/${created.share_id}/pdf`} target="_blank" rel="noopener noreferrer"><Printer className="w-3.5 h-3.5 mr-1.5" /> Print PDF</a></Button>
            </Card>
          )}

          {mine.length > 0 && (
            <Card className="p-5 bg-card">
              <h2 className="text-base font-semibold mb-2" style={{fontFamily: 'Lexend'}}>Your recent checklists</h2>
              <ul className="space-y-2">
                {mine.slice(0, 8).map((s) => (
                  <li key={s.share_id} className="text-sm" data-testid={`share-row-${s.share_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0"><div className="font-medium truncate">{s.client_name}</div><div className="text-xs text-muted-foreground">{s.program_ids.length} programs · {s.claimed_by ? 'Claimed by client' : 'Not yet opened'}</div></div>
                      <div className="flex gap-1 shrink-0">
                        <Button asChild size="sm" variant="ghost" aria-label="Print PDF" data-testid={`share-pdf-btn-${s.share_id}`}><a href={`${API}/share/${s.share_id}/pdf`} target="_blank" rel="noopener noreferrer"><Printer className="w-4 h-4" /></a></Button>
                        <Button size="sm" variant="ghost" onClick={() => setOpenProgress(openProgress === s.share_id ? null : s.share_id)} aria-label="View progress" data-testid={`share-progress-btn-${s.share_id}`}><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => copy(s.share_id)} aria-label="Copy link"><Copy className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    {openProgress === s.share_id && <ShareProgress shareId={s.share_id} />}
                    {openProgress === s.share_id && <NoteComposer shareId={s.share_id} claimed={!!s.claimed_by} />}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
